import { Misuse, PlayFilled, StopFilled, TrashCan } from '@carbon/icons-react'
import React, { useRef, useState, useEffect } from 'react'
import { ONETIME } from '../../constants'
import { KnovaText } from '../../BasicUIComponents/KnovaText'

const CanvasDrawing = ({
  initialData = [],
  currentShape = 'stroke',
  strokeStyle = 'black', // Comes from parent
  lineWidth = 2, // Comes from parent
  currentSpeed = ONETIME,
  fromLayout = true,
  isSideMenu,
  isAnswerPad,
  themeColors,
  strokeReplayStarted,
  onSaveData,
  getStrokeReplayFinished,
  onToastTrigger,
  themeFont,
  getReplyStatus,
  isAudioPlaying,
  completed,
  isActive,
  disableWriting
}) => {
  const canvasRef = useRef(null)
  const contextRef = useRef(null)
  const isDrawing = useRef(false)
  const [strokeArray, setStrokeArray] = useState([])
  const [strokeActions, setStrokeActions] = useState([])
  const [pointsArray, setPointsArray] = useState([])
  //const [currentShape, setCurrentShape] = useState('stroke') // 'stroke', 'line', 'circle', 'rectangle'
  const [isReplaying, setIsReplaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [selectionBox, setSelectionBox] = useState(null)
  const [selectedStrokes, setSelectedStrokes] = useState([])
  const headModalContainer = useRef()
  const [canvasDimension, setCanvasAreaDimensions] = useState({
    width: 0,
    height: 0
  })

  const [isDragStarted, setIsDragStarted] = useState(false)
  const [onBlurCall, setOnBlurCall] = useState(true)

  const [resize, setResize] = useState(0)

  //Call when browser is resizing
  useEffect(() => {
    const resizeListener = () => {
      setResize((resize) => resize + 1)
    }

    // set resize listener
    window.addEventListener('resize', resizeListener)
    window.history.pushState(null, null, window.location.pathname)
    return () => {
      window.removeEventListener('resize', resizeListener)
    }
  }, [])

  //get canvas width and height
  useEffect(() => {
    if (headModalContainer && headModalContainer.current) {
      var ansContWidth = headModalContainer.current.clientWidth
      var ansContHeight = headModalContainer.current.clientHeight
      let canvasAreaDimensions = {
        width: ansContWidth,
        height: ansContHeight
      }
      setCanvasAreaDimensions(canvasAreaDimensions)
    }
  }, [])

  useEffect(() => {
    if (resize > 0) {
      var ansContWidth = headModalContainer.current.clientWidth
      var ansContHeight = headModalContainer.current.clientHeight
      let canvasAreaDimensions = { width: ansContWidth, height: ansContHeight }
      setCanvasAreaDimensions(canvasAreaDimensions)
    }
  }, [resize])

  useEffect(() => {
    if (onBlurCall) {
      if (fromLayout && contextRef) {
        if (initialData && initialData !== '') {
          let isArray = Array.isArray(initialData)
          let replayRectData = {}
          if (isArray) {
            if (initialData.length > 0) {
              replayRectData = initialData[0]
              // let strokeArrayCopy = initialData[0].strokeArray
              // let strokeActionCopy = initialData[0].strokeActions
              // setStrokeArray(strokeArrayCopy)
              // setStrokeActions(strokeActionCopy)
              //redrawCanvas()
            }
          } else {
            replayRectData = initialData
          }
          if (Object.keys(replayRectData).length > 0) {
            let strokeArrayCopy = replayRectData.strokeArray
            let strokeActionCopy = replayRectData.strokeActions
            setStrokeArray(strokeArrayCopy)
            setStrokeActions(strokeActionCopy)
          }
          setOnBlurCall(false)
        }
      }
    }
  }, [contextRef, initialData, onBlurCall])

  //Set height and width for canvas
  useEffect(() => {
    if (canvasRef.current && canvasRef.current !== null) {
      const canvas = canvasRef.current
      canvas.width = canvasDimension.width
      canvas.height = canvasDimension.height
      canvas.style.width = `${canvasDimension.width}px`
      canvas.style.height = `${canvasDimension.height}px`
      const context = canvas.getContext('2d')
      contextRef.current = context
      redrawCanvas()
      // setContext(canvas.getContext('2d'))
    }
  }, [canvasRef, canvasDimension, strokeActions])

  // Redraw the canvas with all strokes
  const redrawCanvas = (strokesToDraw = strokeArray) => {
    const context = contextRef.current
    context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height) // Clear the canvas

    // Redraw only the strokes passed to the function
    strokesToDraw.forEach((stroke) => {
      if (stroke.isDeleted) return // Skip deleted strokes

      context.strokeStyle = stroke.strokeStyle || 'black'
      context.lineWidth = stroke.lineWidth || 2

      if (stroke.type === 'stroke') {
        // Redraw freehand strokes
        context.beginPath()
        stroke.points.forEach((point, index) => {
          if (index === 0) {
            context.moveTo(point.x, point.y)
          } else {
            context.lineTo(point.x, point.y)
          }
        })
        context.stroke()
      } else if (stroke.type === 'line') {
        // Redraw lines
        const [start, end] = stroke.points
        context.beginPath()
        context.moveTo(start.x, start.y)
        context.lineTo(end.x, end.y)
        context.stroke()
      } else if (stroke.type === 'rectangle') {
        // Redraw rectangles
        const [start, end] = stroke.points
        const width = end.x - start.x
        const height = end.y - start.y
        context.beginPath()
        context.rect(start.x, start.y, width, height)
        context.stroke()
      } else if (stroke.type === 'circle') {
        // Redraw circles
        const [start, end] = stroke.points
        const radius = Math.sqrt(
          Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        )
        context.beginPath()
        context.arc(start.x, start.y, radius, 0, Math.PI * 2)
        context.stroke()
      }
    })

    // Draw the selection box, if it exists
    if (selectionBox) {
      drawSelectionBox(selectionBox)
    }
  }

  // Draw an item (stroke or shape)
  const drawItem = (item, color) => {
    const context = contextRef.current
    context.strokeStyle = color || item.strokeStyle
    context.lineWidth = item.lineWidth
    context.beginPath()

    if (item.type === 'stroke') {
      item.points.forEach((point, index) => {
        if (index === 0) {
          context.moveTo(point.x, point.y)
        } else {
          context.lineTo(point.x, point.y)
        }
      })
    } else if (item.type === 'line') {
      context.moveTo(item.points[0].x, item.points[0].y)
      context.lineTo(item.points[1].x, item.points[1].y)
    } else if (item.type === 'rectangle') {
      const [start, end] = item.points
      context.rect(start.x, start.y, end.x - start.x, end.y - start.y)
    } else if (item.type === 'circle') {
      const [start, end] = item.points
      const radius = Math.sqrt(
        Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
      )
      context.arc(start.x, start.y, radius, 0, Math.PI * 2)
    }

    context.stroke()
  }

  // Draw selection box
  const drawSelectionBox = (box) => {
    const context = contextRef.current
    context.strokeStyle = '#4A22FE'
    context.lineWidth = 1
    context.setLineDash([5, 5])
    context.strokeRect(
      box.startX,
      box.startY,
      box.endX - box.startX,
      box.endY - box.startY
    )
    context.setLineDash([])
  }

  const canvasClick = () => {
    if (currentShape === 'select' && !isDragStarted) {
      onToastTrigger('Draw a rectangle to select and delete strokes.')
    }
  }

  // Start drawing
  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent
    if (currentShape === 'select') {
      setSelectionBox({
        startX: offsetX,
        startY: offsetY,
        endX: offsetX,
        endY: offsetY
      })
    } else {
      isDrawing.current = true
      setPointsArray([{ x: offsetX, y: offsetY }])
    }
  }
  // During drawing
  // Draw a stroke or shape
  const draw = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent

    if (selectionBox) {
      // Update selection box coordinates during selection
      setSelectionBox((prev) => ({ ...prev, endX: offsetX, endY: offsetY }))
      redrawCanvas()
      return
    }

    if (!isDrawing.current) return

    // Update points for current stroke or shape
    const updatedPoints = [...pointsArray, { x: offsetX, y: offsetY }]
    setPointsArray(updatedPoints)

    // Redraw the canvas
    redrawCanvas()

    // Preview the current shape or stroke
    const context = contextRef.current
    context.strokeStyle = strokeStyle
    context.lineWidth = lineWidth
    context.beginPath()

    if (currentShape === 'stroke') {
      // Draw freehand stroke
      updatedPoints.forEach((point, index) => {
        if (index === 0) {
          context.moveTo(point.x, point.y)
        } else {
          context.lineTo(point.x, point.y)
        }
      })
    } else if (currentShape === 'line') {
      // Draw a preview of the line
      const start = updatedPoints[0]
      context.moveTo(start.x, start.y)
      context.lineTo(offsetX, offsetY)
    } else if (currentShape === 'rectangle') {
      // Draw a preview of the rectangle
      const start = updatedPoints[0]
      const width = offsetX - start.x
      const height = offsetY - start.y
      context.rect(start.x, start.y, width, height)
    } else if (currentShape === 'circle') {
      // Draw a preview of the circle
      const start = updatedPoints[0]
      const radius = Math.sqrt(
        Math.pow(offsetX - start.x, 2) + Math.pow(offsetY - start.y, 2)
      )
      context.arc(start.x, start.y, radius, 0, Math.PI * 2)
    }

    context.stroke()
  }

  // Finish drawing
  const finishDrawing = () => {
    if (selectionBox) {
      // Finalize selection
      const { startX, startY, endX, endY } = selectionBox
      const selected = strokeArray.filter((item) =>
        item.points.some(
          (point) =>
            point.x >= Math.min(startX, endX) &&
            point.x <= Math.max(startX, endX) &&
            point.y >= Math.min(startY, endY) &&
            point.y <= Math.max(startY, endY)
        )
      )
      if (selected.length > 0) {
        setIsDragStarted(true)
        setTimeout(() => {
          setIsDragStarted(false)
        }, 500)
      }
      setSelectedStrokes(selected.map((item) => item.id))
      setSelectionBox(null)
      redrawCanvas()
      return
    }
    if (!isDrawing.current) return

    isDrawing.current = false

    if (pointsArray.length === 0) return

    // Determine shape data
    let shapePoints = []
    if (
      currentShape === 'line' ||
      currentShape === 'rectangle' ||
      currentShape === 'circle'
    ) {
      const [start] = pointsArray
      const end = pointsArray[pointsArray.length - 1]
      shapePoints = [start, end]
    } else {
      shapePoints = pointsArray
    }

    const newStroke = {
      id: Date.now(),
      points: shapePoints,
      strokeStyle,
      lineWidth,
      isDeleted: false,
      type: currentShape
    }

    // Update state
    setStrokeArray((prev) => [...prev, newStroke])
    setStrokeActions((prev) => [
      ...prev,
      { Action: 'add', StrokeID: newStroke.id }
    ])
    onSaveData(
      [...strokeArray, newStroke],
      [...strokeActions, { Action: 'add', StrokeID: newStroke.id }]
    )
    setPointsArray([])
    redrawCanvas() // Redraw after adding the new stroke
  }

  // Delete selected items
  const deleteSelected = () => {
    const updatedStrokes = strokeArray.map((stroke) =>
      selectedStrokes.includes(stroke.id)
        ? { ...stroke, isDeleted: true }
        : stroke
    )

    const updatedActions = [
      ...strokeActions,
      ...selectedStrokes.map((id) => ({ Action: 'delete', StrokeID: id }))
    ]

    setStrokeArray(updatedStrokes)
    setStrokeActions(updatedActions)
    setSelectedStrokes([])
    onSaveData(updatedStrokes, updatedActions)
    redrawCanvas()
  }

  const replayAbortController = useRef(null) // Controller for aborting the replay process

  const replayStrokes = async () => {
    if (replayAbortController.current) {
      replayAbortController.current.abort() // Abort any previous replay if running
    }

    replayAbortController.current = new AbortController()
    const { signal } = replayAbortController.current

    setIsReplaying(true)
    setIsPaused(false)

    const context = contextRef.current
    context.clearRect(0, 0, canvasDimension.width, canvasDimension.height)

    try {
      for (const action of strokeActions) {
        if (signal.aborted) throw new Error('Replay aborted') // Stop replay immediately if aborted

        // Handle pause functionality
        while (isPaused) {
          if (signal.aborted) throw new Error('Replay aborted')
          await new Promise((resolve) => setTimeout(resolve, 100)) // Wait until resumed
        }

        if (action.Action === 'add') {
          // Draw the stroke
          const stroke = strokeArray.find(
            (stroke) => stroke.id === action.StrokeID
          )
          if (stroke && !stroke.isDeleted) {
            drawItem(stroke) // Draw the stroke
            await delayWithSignal(currentSpeed, signal) // Delay to show the stroke
          }
        } else if (action.Action === 'delete') {
          // Temporarily display the stroke, then hide it
          const stroke = strokeArray.find(
            (stroke) => stroke.id === action.StrokeID
          )
          if (stroke) {
            drawItem(stroke) // Briefly show the stroke before deletion
            await delayWithSignal(currentSpeed, signal) // Delay to simulate visibility before hiding
            stroke.isDeleted = true // Mark as deleted
            redrawCanvas() // Redraw canvas without the deleted stroke
            await delayWithSignal(currentSpeed, signal) // Delay to show deletion
          }
        }
      }
    } catch (error) {
      if (error.message !== 'Replay aborted') {
        console.error('Unexpected error during replay:', error)
      }
    } finally {
      setIsReplaying(false)
    }
  }

  const delayWithSignal = (ms, signal) => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(resolve, ms)
      signal.addEventListener('abort', () => {
        clearTimeout(timeout)
        reject(new Error('Replay aborted'))
      })
    })
  }

  // Function to stop the replay process
  const stopReplay = () => {
    if (replayAbortController.current) {
      console.log('whileStopes', replayAbortController.current)
      replayAbortController.current.abort() // Signal the replay to stop
      redrawCanvas()
    }
    setIsReplaying(false)
  }

  const togglePauseReplay = () => {
    setIsPaused((prev) => !prev)
  }

  useEffect(() => {
    if (isReplaying) {
      replayStrokes()
    }
    if (getReplyStatus) {
      getReplyStatus(isReplaying)
    }
  }, [isReplaying])

  function renderCanvaFloating() {
    if (strokeReplayStarted) {
      return (
        <div className='canvas-floating-cont'>
          {!isReplaying ? (
            <PlayFilled
              className='canvas-floating-icon'
              onClick={() => {
                setIsReplaying(true)
              }}
            />
          ) : (
            <StopFilled className='canvas-floating-icon' onClick={stopReplay} />
          )}

          {/* {isReplaying && !isPaused && (
            <PauseOutlineFilled
              className='canvas-floating-icon'
              onClick={togglePauseReplay}
            />
          )}
          {isReplaying && isPaused && (
            <PlayFilled
              className='canvas-floating-icon'
              onClick={togglePauseReplay}
            />
          )} */}
          <Misuse
            className='canvas-floating-icon'
            onClick={() => {
              stopReplay()
              getStrokeReplayFinished()
            }}
          />
        </div>
      )
    } else {
      if (selectedStrokes.length > 0) {
        return (
          <div className='canvas-floating-cont'>
            <TrashCan
              className='canvas-floating-icon'
              onClick={deleteSelected}
            />
          </div>
        )
      } else {
        return null
      }
    }
  }

  return (
    <div
      ref={headModalContainer}
      className='sketch-pad-container'
      style={{
        borderWidth: isAnswerPad
          ? '0px'
          : isSideMenu
          ? '1px'
          : fromLayout
          ? '2px'
          : '0px',
        borderStyle: isAnswerPad
          ? 'none'
          : isSideMenu
          ? 'solid'
          : fromLayout
          ? 'solid'
          : 'unset',
        borderColor: isSideMenu
          ? themeColors.outline
          : fromLayout
          ? themeColors.outline
          : 'unset',
        backgroundColor: isSideMenu
          ? 'white'
          : fromLayout
          ? themeColors.backgroundContainer
          : 'unset',
        borderRadius: isSideMenu ? '5px' : fromLayout ? '12px' : '0px'
      }}
    >
      <canvas
        ref={canvasRef}
        className='sketch-pad-canvas'
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={finishDrawing}
        onMouseLeave={finishDrawing}
        onClick={canvasClick}
        style={{
          pointerEvents: isReplaying || disableWriting ? 'none' : 'auto'
        }}
      />
      {renderCanvaFloating()}
      {strokeActions.length <= 0 && (
        <div className='empty-text-cont'>
          <KnovaText
            text='Replay work area'
            textColor='black'
            textFontSize={isSideMenu ? '12px' : '24px'}
            textFontFamily={themeFont.bodyFont}
            textFontWeight='400'
            textOpacity={0.3}
            textAlign='center'
          />
        </div>
      )}
    </div>
  )
}
export default CanvasDrawing
